import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Hospital, MapPin, Phone, Info, ShieldCheck, ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import ProviderIdentifierPanel from '../components/ProviderIdentifierPanel';
import PostAcuteTab from '../components/PostAcuteTab';
import { api } from '../api';

function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }

function Star({ r }) {
  if (r == null) return <span className="text-[#A8B0AA]">no rating</span>;
  return <span className="font-semibold text-[#A06628] text-[18px]">{r}<span className="text-[#D4A85C] ml-1">★</span></span>;
}

export default function HospitalDetailPage() {
  const { ccn } = useParams();
  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.hospital(ccn).then((d) => {
      setH(d); setLoading(false);
      try {
        window.gtag && window.gtag('event', 'profile_view', {
          entity_type: 'hospital', ccn: d?.ccn, state: d?.state,
          rating: d?.overall_rating || null,
        });
      } catch (_) {}
    }).catch(() => setLoading(false));
  }, [ccn]);

  if (loading) return <div className="min-h-screen"><Navbar /><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><div className="text-sm text-[#7A857F]">Loading hospital…</div></main></div>;
  if (!h) return <div className="min-h-screen"><Navbar /><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><h1 className="font-display text-3xl">Hospital not found</h1></main></div>;

  const linked = h.linked_facilities || [];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Hospitals', to: '/hospitals' }, { label: h.name }]} />

        <header className="mt-2 mb-6 flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wider bg-[#E0EAF8] text-[#34538F]">
              <Hospital size={11} /> Acute-care hospital · CCN {h.ccn}
            </div>
            {h.npi && (
              <span className="ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-white border border-[#34538F]/30" data-testid="hospital-header-npi">
                <span className="text-[10.5px] font-semibold uppercase tracking-wider text-[#34538F]">Org NPI</span>
                <span className="text-[10.5px] font-mono text-[#1A2622]">{h.npi}</span>
              </span>
            )}
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-[#1A2622] mt-2">{h.name}</h1>
            {h.trade_names?.length > 0 && (
              <div className="mt-1.5 text-[13px] text-[#3A4A44]" data-testid="hospital-trade-names">
                <span className="text-[10.5px] font-semibold uppercase tracking-wider text-[#7A857F] mr-1.5">Also known as</span>
                {h.trade_names.slice(0, 4).join(' · ')}
              </div>
            )}
            <div className="mt-2 flex flex-wrap gap-3 text-[12.5px] text-[#5A6660]">
              {h.address && <span className="flex items-center gap-1"><MapPin size={12} /> {h.address}, {h.city}, {h.state} {h.zip5}</span>}
              {h.phone && <span className="flex items-center gap-1"><Phone size={12} /> {h.phone}</span>}
            </div>
          </div>
          <Link to="/hospitals" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-[#E5DCC4] text-[#3A4A44] text-[12.5px] hover:bg-[#F5EFDD]">
            <ArrowLeft size={13} /> All hospitals
          </Link>
        </header>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Overall rating</div>
            <div className="mt-1"><Star r={h.overall_rating} /></div>
          </div>
          <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Type</div>
            <div className="mt-1 text-[13px] text-[#1A2622]">{h.type || '—'}</div>
          </div>
          <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Ownership</div>
            <div className="mt-1 text-[13px] text-[#1A2622] truncate" title={h.ownership}>{h.ownership || '—'}</div>
          </div>
          <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Emergency services</div>
            <div className={`mt-1 text-[13px] font-semibold ${h.emergency_services ? 'text-[#2D5F4F]' : 'text-[#A8B0AA]'}`}>{h.emergency_services ? 'Yes' : 'No'}</div>
          </div>
          <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Linked SNFs</div>
            <div className="mt-1 text-[13px] font-semibold text-[#1A2622]">{linked.length}</div>
          </div>
        </div>

        {/* Provider Identifier & Entity Intelligence (additive — NPPES enrichment) */}
        <div className="mt-5">
          <ProviderIdentifierPanel
            entityType="hospital"
            entityId={h.ccn}
            fallback={{ ccn: h.ccn, ownership: h.ownership }}
          />
        </div>

        {/* Readmissions */}
        <div className="mt-5 rounded-xl bg-white border border-[#E5DCC4] p-5">
          <h3 className="font-display text-base font-semibold text-[#1A2622]">CMS Readmission measures</h3>
          <p className="mt-1 text-[12px] text-[#7A857F]">From the Hospital General Information rollup — count of measures where this hospital is meaningfully better or worse than the national rate.</p>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-lg border border-[#F0E7D2]"><div className="text-[10.5px] uppercase text-[#7A857F]">Measures reported</div><div className="font-display text-xl text-[#1A2622]">{fmtInt(h.readm_total)}</div></div>
            <div className="p-3 rounded-lg border border-[#F0E7D2]"><div className="text-[10.5px] uppercase text-[#7A857F]">Better than national</div><div className="font-display text-xl text-[#2D5F4F]">{fmtInt(h.readm_better)}</div></div>
            <div className="p-3 rounded-lg border border-[#F0E7D2]"><div className="text-[10.5px] uppercase text-[#7A857F]">No different</div><div className="font-display text-xl text-[#A06628]">{fmtInt(h.readm_same)}</div></div>
            <div className="p-3 rounded-lg border border-[#F0E7D2]"><div className="text-[10.5px] uppercase text-[#7A857F]">Worse than national</div><div className="font-display text-xl text-[#C75D5D]">{fmtInt(h.readm_worse)}</div></div>
          </div>
        </div>

        {/* Linked SNFs */}
        <div className="mt-5 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-base font-semibold text-[#1A2622]">SNFs in this hospital's catchment</h3>
            <span className="ml-auto inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider text-[#7A857F]">
              <ShieldCheck size={11} /> public-data referral catchment
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                <tr>
                  <th className="text-left px-4 py-2.5">SNF</th>
                  <th className="text-left px-4 py-2.5">City / State</th>
                  <th className="text-right px-4 py-2.5">Confirmed shared</th>
                  <th className="text-right px-4 py-2.5">Likely shared</th>
                  <th className="text-left px-4 py-2.5">Proximity</th>
                  <th className="text-right px-4 py-2.5">Score</th>
                </tr>
              </thead>
              <tbody>
                {linked.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#7A857F]">No SNFs linked yet.</td></tr>}
                {linked.map((s) => (
                  <tr key={s.ccn_snf} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`hosp-snf-${s.ccn_snf}`}>
                    <td className="px-4 py-2.5"><Link to={`/facilities/${s.ccn_snf}`} className="text-[#1A2622] font-medium hover:text-[#34538F]">{s.snf_name}</Link></td>
                    <td className="px-4 py-2.5 text-[#3A4A44]">{s.snf_city}, {s.snf_state}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-[#2D5F4F] tabular-nums">{fmtInt(s.confirmed_shared_physicians)}</td>
                    <td className="px-4 py-2.5 text-right text-[#A06628] tabular-nums">{fmtInt(s.likely_shared_physicians)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${s.confidence === 'high' ? 'bg-[#E5F0EA] text-[#2D5F4F]' : 'bg-[#FBE6CC] text-[#A06628]'}`}>{s.confidence}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-[#34538F] font-semibold">{s.alignment_score?.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span>
            <strong>Methodology:</strong> {h.methodology}
            <strong> Source:</strong> CMS Hospital General Information.
            <strong> Secondary:</strong> {h.secondary_source}.
            {h.updated_at && <> Updated {new Date(h.updated_at).toLocaleDateString()}.</>}
          </span>
        </div>

        <div className="mt-8">
          <h2 className="font-display text-xl font-semibold text-[#1A2622] mb-1">Post-Acute Network</h2>
          <p className="text-[12px] text-[#7A857F] mb-3">Home Health &amp; Hospice agencies within 25 miles of this hospital.</p>
          <PostAcuteTab kind="hospital" ccn={h.ccn} />
        </div>

        {h.hrrp && <HRRPCard hrrp={h.hrrp} />}
        {h.discharges && <DischargeCategoriesCard discharges={h.discharges} />}
        <NearbySNFsSection ccn={h.ccn} />
      </main>
      <Footer />
    </div>
  );
}

function DischargeCategoriesCard({ discharges }) {
  const cats = discharges.by_category || [];
  const total = discharges.total_discharges || cats.reduce((s, c) => s + c.discharges, 0);
  return (
    <div className="mt-8" data-testid="discharge-categories">
      <h2 className="font-display text-xl font-semibold text-[#1A2622] mb-1">
        Diagnoses This Hospital Treats
      </h2>
      <p className="text-[12px] text-[#7A857F] mb-3">
        {total.toLocaleString()} Medicare inpatient discharges in {discharges.source_period || 'latest CMS year'}.
        Grouped into plain-English clinical categories.
      </p>
      <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
        <div className="space-y-3">
          {cats.slice(0, 12).map((c) => {
            const pct = total ? (100 * c.discharges / total) : 0;
            return (
              <div key={c.category}>
                <div className="flex items-center justify-between gap-3 text-[13px]">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#1A2622]">{c.category}</div>
                    {c.examples && c.examples.length > 0 && (
                      <div className="text-[11px] text-[#7A857F] truncate">
                        e.g. {c.examples.slice(0, 2).join(' · ')}
                      </div>
                    )}
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <div className="font-display text-[16px] text-[#1A2622] tabular-nums">
                      {c.discharges.toLocaleString()}
                    </div>
                    <div className="text-[11px] text-[#7A857F]">{pct.toFixed(1)}% · {c.drg_count} codes</div>
                  </div>
                </div>
                <div className="mt-1 h-1.5 bg-[#F5EFDD] rounded overflow-hidden">
                  <div className="h-full bg-[#4A7C5E]" style={{ width: `${Math.max(2, pct)}%` }}/>
                </div>
              </div>
            );
          })}
        </div>
        {discharges.avg_medicare_pymt && (
          <div className="mt-5 pt-4 border-t border-[#F0E7D2] text-[12px] text-[#5A6660]">
            Average Medicare payment per discharge:{' '}
            <strong className="text-[#1A2622]">${Math.round(discharges.avg_medicare_pymt).toLocaleString()}</strong>
          </div>
        )}
        <div className="mt-3 text-[11px] text-[#7A857F]">
          Source: CMS Medicare Inpatient Hospitals — by Provider and Service PUF.
        </div>
      </div>
    </div>
  );
}

function NearbySNFsSection({ ccn }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    setLoading(true);
    api.hospitalNearbySNFs(ccn, { limit: 25 })
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [ccn]);

  if (loading) return <div className="mt-8 text-[12px] text-[#7A857F]">Loading nearby SNFs…</div>;
  const snfs = data?.snfs || [];
  if (!snfs.length) return null;

  return (
    <section className="mt-8" data-testid="nearby-snfs">
      <h2 className="font-display text-xl font-semibold text-[#1A2622] mb-1">
        Nearby SNFs — Ranked for Partnership
      </h2>
      <p className="text-[12px] text-[#7A857F] mb-3">
        Every nursing facility within range, scored 0-100 on the 8 metrics
        hospitals use to pick post-acute partners (readmission, infection, staffing,
        turnover, star rating, fines, agency staff). Higher = better partner.
      </p>
      <div className="p-5 rounded-xl bg-white border border-[#E5DCC4] overflow-x-auto">
        <table className="w-full text-[12.5px] min-w-[800px]">          <thead className="text-[10.5px] uppercase tracking-wider text-[#7A857F] bg-[#F5EFDD]">
            <tr>
              <th className="text-left px-3 py-2">Rank</th>
              <th className="text-left px-3 py-2">SNF</th>
              <th className="text-left px-3 py-2">Star</th>
              <th className="text-right px-3 py-2">Readm.</th>
              <th className="text-right px-3 py-2">Infection</th>
              <th className="text-right px-3 py-2">Nursing hrs</th>
              <th className="text-right px-3 py-2">RN hrs</th>
              <th className="text-right px-3 py-2">Turnover</th>
              <th className="text-right px-3 py-2 bg-[#1A2622] text-[#FBF6EC]">Partner score</th>
            </tr>
          </thead>
          <tbody>
            {snfs.map((s, i) => {
              const score = s.partner_score;
              const bg = score == null ? '' : score >= 70 ? 'bg-[#E5F0EA]' : score >= 50 ? 'bg-[#F5EFDD]' : '';
              const scoreColor = score == null ? '#5A6660' : score >= 70 ? '#2D5F4F' : score >= 50 ? '#A06628' : '#7A2E0F';
              return (
                <tr key={s.ccn} className={`border-t border-[#F0E7D2] ${bg}`} data-testid={`nearby-snf-${s.ccn}`}>
                  <td className="px-3 py-2.5 text-[#5A6660] tabular-nums">#{i + 1}</td>
                  <td className="px-3 py-2.5">
                    <Link to={`/facilities/${s.ccn}`} className="font-semibold text-[#1A2622] hover:text-[#2D5F4F]">
                      {s.name}
                    </Link>
                    {s.city && <div className="text-[11px] text-[#7A857F]">{s.city}, {s.state} · {s.beds} beds</div>}
                  </td>
                  <td className="px-3 py-2.5">{s.overall_star ? <span className="font-semibold">{s.overall_star}★</span> : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{s.readmission_rate != null ? `${(s.readmission_rate * 100).toFixed(1)}%` : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{s.hai_rate != null ? `${(s.hai_rate * 100).toFixed(1)}%` : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{s.hprd_total != null ? s.hprd_total.toFixed(2) : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{s.hprd_rn != null ? s.hprd_rn.toFixed(2) : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{s.turnover_rate != null ? `${(s.turnover_rate * 100).toFixed(0)}%` : '—'}</td>
                  <td className="px-3 py-2.5 text-right font-display text-[16px] tabular-nums" style={{ color: scoreColor }}>
                    {score != null ? score.toFixed(0) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[10.5px] text-[#A8B0AA] sm:hidden text-center">← Swipe to see all 8 metrics →</div>
      <div className="mt-3 text-[11px] text-[#7A857F] leading-relaxed max-w-3xl">
        {data?.methodology}
      </div>
    </section>
  );
}

function HRRPCard({ hrrp }) {
  const worst = hrrp.worst_excess_ratio;
  const avg = hrrp.avg_excess_ratio;
  const sorted = (hrrp.measures || []).slice().sort((a, b) =>
    (b.excess_ratio ?? 0) - (a.excess_ratio ?? 0),
  );
  const plain = (r) => {
    if (r == null) return '—';
    if (r > 1.10) return 'Much worse than expected';
    if (r > 1.02) return 'Worse than expected';
    if (r >= 0.98) return 'About as expected';
    if (r >= 0.90) return 'Better than expected';
    return 'Much better than expected';
  };
  return (
    <div className="mt-8" data-testid="hrrp-card">
      <h2 className="font-display text-xl font-semibold text-[#1A2622] mb-1">Hospital Return-to-Hospital Rate</h2>
      <p className="text-[12px] text-[#7A857F] mb-3">
        How often patients come back to this hospital within 30 days of being discharged. If worse than Medicare expects, Medicare cuts the hospital&apos;s payment the next year — which gives them a reason to look for better SNF partners.
      </p>
      <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Overall</div>
            <div className="mt-1 font-semibold text-[14px]" style={{ color: hrrp.under_penalty ? '#7A2E0F' : '#2D5F4F' }}>
              {hrrp.under_penalty ? 'Under Medicare penalty' : 'No penalty this year'}
            </div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Worst condition</div>
            <div className="mt-1 font-semibold text-[14px]" style={{ color: (worst || 0) > 1.0 ? '#7A2E0F' : '#2D5F4F' }}>
              {plain(worst)}
            </div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Average</div>
            <div className="mt-1 font-semibold text-[14px] text-[#1A2622]">{plain(avg)}</div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Conditions tracked</div>
            <div className="mt-1 font-display text-2xl tracking-tight text-[#1A2622]">{hrrp.measure_count || 0}</div>
          </div>
        </div>
        <div className="-mx-5 overflow-x-auto">
          <table className="w-full mt-4 text-[12.5px] min-w-[560px] px-5">
            <thead className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">
              <tr>
                <th className="text-left py-2 px-5">Condition</th>
                <th className="text-right px-3">How this hospital does</th>
                <th className="text-right px-3">Their rate</th>
                <th className="text-right px-3 pr-5">Expected</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((m) => {
                const ratio = m.excess_ratio;
                const color = ratio == null ? '#5A6660' : ratio > 1.0 ? '#7A2E0F' : '#2D5F4F';
                return (
                  <tr key={m.measure} className="border-t border-[#F0E7D2]">
                    <td className="py-2.5 px-5 text-[#1A2622] whitespace-nowrap">{m.name}</td>
                    <td className="py-2.5 px-3 text-right font-semibold" style={{ color }}>{plain(ratio)}</td>
                    <td className="py-2.5 px-3 text-right text-[#5A6660] tabular-nums">{m.predicted_rate != null ? `${m.predicted_rate.toFixed(1)}%` : '—'}</td>
                    <td className="py-2.5 px-3 pr-5 text-right text-[#5A6660] tabular-nums">{m.expected_rate != null ? `${m.expected_rate.toFixed(1)}%` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-[10.5px] text-[#A8B0AA] sm:hidden text-center">← Swipe to see full table →</div>
        <div className="mt-4 text-[11px] text-[#5A6660] leading-relaxed">
          <strong className="text-[#3A4A44]">How to read this:</strong> &quot;Expected&quot; is what Medicare predicts
          based on how sick each patient was. &quot;Their rate&quot; is what actually happened. When theirs is higher than
          expected, they&apos;re under penalty next year. Hospitals under penalty are usually the ones most willing to
          partner with better post-acute providers.
        </div>
      </div>
    </div>
  );
}
